import { router, useLocalSearchParams } from 'expo-router';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { CreditCardFormData, CreditCardInput, CreditCardView } from 'react-native-credit-card-input';
import { TextInput } from 'react-native-gesture-handler';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useAddCreditCard } from '@/api/hooks/paymentAccountsHooks';
import { BillingInfoResponse } from '@/api/models/individualModels';
import { card_back, card_front } from '@/assets/images';
import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import GoogleAutoComplete from '@/components/inputs/GoogleAutocomplete';
import Text from '@/components/Text';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useColor } from '@/hooks/useColor';
import { parseAddressComponents } from '@/utils/addressFromAddressComponents';
import { decryptObject, encryptObject } from '@/utils/crypto';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';

import { TokenizedCreditCardData } from './creditCardList';

const AddCreditCard = () => {
  const colors = useColor();

  const { payload } = useLocalSearchParams<{
    payload: string;
  }>();
  const { mutateAsync: addCreditCard, isPending } = useAddCreditCard();

  const { showSuccess } = useEndFlowModal();

  const [payloadState, setPayloadState] = useState<{
    donationData: string;
    fromBillingData: string;
    individualNuveiUser: string;
    fromIOS?: string;
    influencerCode?: string;
    routeWithSelectedMethod?: string;
    subscriptionItem?: string;
    showId?: string;
  }>();

  const [parsedIndividualNuveiUser, setParsedIndividualNuveiUser] = useState<BillingInfoResponse>();
  const [parsedDonationData, setParsedDonationData] = useState<{
    individualUserId: string;
    nonProfitOrgId: string;
    giftType: string;
    giftRecurringOption?: string;
    optIn?: boolean;
    amount: string;
    whereToDonate: string;
  }>();
  const [parsedSubscriptionItem, setParsedSubscriptionItem] = useState<{
    nonProfitOrgId: string;
  }>();

  const [cardData, setCardData] = useState<CreditCardFormData | undefined>();
  const [focusedField, setFocusedField] = useState<'number' | 'expiry' | 'cvc' | 'name'>();
  const [cardHolderName, setCardHolderName] = useState<string>(parsedIndividualNuveiUser?.customerName ?? '');
  const [initialBillingAddress, setInitialBillingAddress] = useState<string>(
    parsedIndividualNuveiUser?.street1 !== undefined
      ? `${parsedIndividualNuveiUser?.street1}, ${parsedIndividualNuveiUser?.city}, ${parsedIndividualNuveiUser?.stateId} ${parsedIndividualNuveiUser?.zipCode}`
      : ''
  );
  const [billingAddress, setBillingAddress] = useState<string>(parsedIndividualNuveiUser?.street1 ?? '');
  const [zipCode, setZipCode] = useState<string>(
    parsedIndividualNuveiUser?.street1 ? parsedIndividualNuveiUser?.zipCode : ''
  );
  const [addressError, setAddressError] = useState<string | undefined>(billingAddress && undefined);

  useEffect(() => {
    const loadPayloadParams = async () => {
      const payloadObject = await decryptObject<{
        donationData: string;
        fromBillingData: string;
        individualNuveiUser: string;
        fromIOS?: string;
        influencerCode?: string;
        routeWithSelectedMethod?: string;
        subscriptionItem?: string;
        showId?: string;
      }>(decodeURIComponent(payload));

      if (payloadObject) {
        setPayloadState(payloadObject);
        const individualNuveiUserObject = safeJsonParse<BillingInfoResponse>(payloadObject.individualNuveiUser ?? '');
        setParsedIndividualNuveiUser(individualNuveiUserObject);
        setParsedDonationData(
          safeJsonParse<{
            individualUserId: string;
            nonProfitOrgId: string;
            giftType: string;
            giftRecurringOption?: string;
            optIn?: boolean;
            amount: string;
            whereToDonate: string;
          }>(payloadObject.donationData ?? '')
        );
        setParsedSubscriptionItem(
          safeJsonParse<{
            nonProfitOrgId: string;
          }>(payloadObject.subscriptionItem ?? '')
        );

        // Setting initial data to the form
        setCardHolderName(individualNuveiUserObject?.customerName ?? '');
        setInitialBillingAddress(
          individualNuveiUserObject?.street1 !== undefined &&
            !isEmpty(individualNuveiUserObject?.street1) &&
            individualNuveiUserObject?.city !== undefined &&
            !isEmpty(individualNuveiUserObject?.city) &&
            individualNuveiUserObject?.stateId !== undefined &&
            !isEmpty(individualNuveiUserObject?.stateId) &&
            individualNuveiUserObject?.zipCode !== undefined &&
            !isEmpty(individualNuveiUserObject?.zipCode)
            ? `${individualNuveiUserObject?.street1}, ${individualNuveiUserObject?.city}, ${individualNuveiUserObject?.stateId} ${individualNuveiUserObject?.zipCode}`
            : ''
        );
        setBillingAddress(
          individualNuveiUserObject?.street1 !== undefined &&
            !isEmpty(individualNuveiUserObject?.street1) &&
            individualNuveiUserObject?.city !== undefined &&
            !isEmpty(individualNuveiUserObject?.city) &&
            individualNuveiUserObject?.stateId !== undefined &&
            !isEmpty(individualNuveiUserObject?.stateId) &&
            individualNuveiUserObject?.zipCode !== undefined &&
            !isEmpty(individualNuveiUserObject?.zipCode)
            ? `${individualNuveiUserObject?.street1}, ${individualNuveiUserObject?.city}, ${individualNuveiUserObject?.stateId} ${individualNuveiUserObject?.zipCode}`
            : ''
        );
        setZipCode(individualNuveiUserObject?.street1 ? individualNuveiUserObject?.zipCode : '');
        setAddressError(individualNuveiUserObject?.street1 && undefined);
      }
    };

    if (payload) {
      loadPayloadParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  const handleConfirm = () => {
    const sanitaziedCardNumber = cardData?.values.number.replace(/\s+/g, '') ?? '';
    addCreditCard(
      {
        npoId:
          parsedDonationData?.nonProfitOrgId ??
          parsedSubscriptionItem?.nonProfitOrgId ??
          (payloadState?.showId !== undefined ? null : ''),
        card_number: sanitaziedCardNumber,
        expiration_date: cardData?.values.expiry.replace('/', '') ?? '',
        name_on_card: cardHolderName,
        street1: billingAddress ?? '',
        zip_code: zipCode ?? '',
        cvv: cardData?.values.cvc ?? ''
      },
      {
        onSuccess: async (response) => {
          const tokenizedCard: TokenizedCreditCardData = {
            card_key: response.card_key,
            card_number_last_four_digits: sanitaziedCardNumber.substring(sanitaziedCardNumber.length - 4),
            expiration_date: cardData?.values.expiry.replace('/', '') ?? '',
            name_on_card: cardHolderName,
            card_type: cardData?.values.type ?? ''
          };

          showSuccess({ title: i18n.t('end_flow.success_title'), message: i18n.t('payment_methods.card_added') });

          const newPayload = { ...payloadState, tokenizedCard: JSON.stringify(tokenizedCard) };

          const encrypted = await encryptObject(newPayload);

          // If comes from billing end process
          if (payloadState?.fromBillingData === 'true') {
            if (payloadState?.donationData) {
              router.replace({
                pathname: '/(app)/donationPayment',
                params: {
                  payload: encodeURIComponent(encrypted)
                }
              });
            } else {
              // Check if param is route
              router.replace({
                pathname: payloadState?.routeWithSelectedMethod as any,
                params: {
                  payload: encodeURIComponent(encrypted)
                }
              });
            }
          } else {
            // Go to billing with card data to update and end donation
            router.replace({
              pathname: '/(app)/paymentMethods/billingAddress',
              params: {
                payload: encodeURIComponent(encrypted)
              }
            });
          }
        }
      }
    );
  };

  return (
    <SafeAreaView style={styles.flexOne} edges={['bottom']}>
      <View style={styles.container}>
        <CreditCardView
          name={cardHolderName}
          imageFront={card_front}
          imageBack={card_back}
          focusedField={focusedField}
          number={cardData?.values.number}
          expiry={cardData?.values.expiry}
          type={cardData?.values.type}
          cvc={cardData?.values.cvc}
        />

        <FormWrapper scrollEnable={false} horizontalPadding={0}>
          <View style={styles.autoComplete}>
            <GoogleAutoComplete
              initialValue={initialBillingAddress.replace(/,\s*\d{5}(?:-\d{4})?,/, ',')} //Remove zip code from initial value (only visual)
              label={i18n.t('payment_methods.billing_address_label')}
              placeholder={i18n.t('payment_methods.billing_address_placeholder')}
              variant="gray_borders"
              type={'address'}
              showManualEntry
              onChange={(result) => {
                if (!result?.postalCode || result.postalCode === '' || !result.city || result.city === '') {
                  setAddressError(i18n.t('payment_methods.invalid_address'));
                } else if (result) {
                  const components = parseAddressComponents(result.addressComponents);
                  setBillingAddress(
                    `${components.street}, ${components.city}, ${components.state} ${components.postalCode}, ${components.country}`
                  );
                  setZipCode(result?.postalCode ?? '');
                  setAddressError(undefined);
                }
              }}
            />
          </View>

          <Text fontWeight="700" style={styles.inputLabel}>
            {i18n.t('credit_cards.holder_label')}
          </Text>
          <View style={styles.inputContainer}>
            <View style={styles.nameInputContainer}>
              <TextInput
                style={[styles.nameInput, { outlineStyle: 'none' } as any]}
                placeholderTextColor={colors.placeholderText}
                focusable
                onFocus={(_) => setFocusedField('name')}
                value={cardHolderName}
                onChangeText={(value) => setCardHolderName(value)}
                placeholder={i18n.t('credit_cards.holder_label')}
                autoCapitalize={'words'}
                returnKeyLabel={'done'}
                returnKeyType={'done'}
                underlineColorAndroid={'transparent'}
                allowFontScaling={false}
              />
            </View>
          </View>

          <CreditCardInput
            inputStyle={styles.inputContainer}
            labels={{
              number: i18n.t('credit_cards.number_label'),
              expiry: i18n.t('credit_cards.expiry_label'),
              cvc: i18n.t('credit_cards.cvc_label')
            }}
            labelStyle={styles.inputLabel}
            style={styles.cardInput}
            onChange={(data) => setCardData(data)}
            onFocusField={(field) => setFocusedField(field)}
          />

          <View style={styles.errorsContainer}>
            {cardData?.status.number === 'invalid' && (
              <Text fontWeight="700" style={styles.errorText}>
                {i18n.t('credit_cards.number_invalid')}
              </Text>
            )}
            {cardData?.status.expiry === 'invalid' && (
              <Text fontWeight="700" style={styles.errorText}>
                {i18n.t('credit_cards.expiry_invalid')}
              </Text>
            )}
            {cardData?.status.cvc === 'invalid' && (
              <Text fontWeight="700" style={styles.errorText}>
                {i18n.t('credit_cards.cvc_invalid')}
              </Text>
            )}
            {addressError && (
              <Text fontWeight="700" style={styles.errorText}>
                {addressError}
              </Text>
            )}
          </View>

          <BrandedButton
            loading={isPending}
            disabled={
              !cardData?.valid ||
              !cardHolderName ||
              !billingAddress ||
              isEmpty(billingAddress) ||
              !zipCode ||
              isEmpty(zipCode) ||
              addressError !== undefined
            }
            style={styles.doneButton}
            onPress={handleConfirm}>
            {i18n.t('mix.done')}
          </BrandedButton>
        </FormWrapper>
      </View>
    </SafeAreaView>
  );
};

export default AddCreditCard;

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  title: {
    fontSize: 22,
    color: 'black',
    alignSelf: 'center'
  },

  container: {
    flex: 1,
    alignItems: 'center',
    paddingTop: 35,
    gap: 10
  },

  cardInput: {
    width: '100%',
    paddingHorizontal: 0
  },

  doneButton: {
    width: '100%'
  },

  nameInput: {
    flex: 1,
    paddingVertical: 10,
    fontSize: 16
  },

  nameInputLabel: {
    color: 'black',
    fontSize: 14
  },

  nameInputContainer: {
    flexDirection: 'row'
  },

  inputLabel: {
    color: 'black',
    marginBottom: 10,
    fontWeight: 700
  },

  inputContainer: {
    paddingHorizontal: 16,
    borderWidth: 1,
    borderRadius: 8,
    borderColor: 'darkgray',
    fontSize: 16
  },

  errorsContainer: {
    paddingVertical: 10
  },

  errorText: {
    fontSize: 16,
    color: 'red'
  },

  autoComplete: {
    zIndex: 1,
    marginBottom: 10
  }
});
